'use client';
import { useState, useRef } from 'react';
import clsx from 'clsx';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import type { CardData } from './index';
import { CardScroller } from './CardScroller';

gsap.registerPlugin(ScrollTrigger);

export function DesktopCards({ cardData }: { cardData: CardData[] }) {
  const [showImageIndex, setShowImageIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<gsap.core.Timeline | null>(null);

  useGSAP(
    () => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: 'top 80%',
          end: 'bottom 20%',
          toggleActions: 'play pause resume reset',
        },
      });

      timelineRef.current = tl;

      tl.to(
        {},
        {
          duration: 5,
          onUpdate: () => {
            const countdownProgress = 1 - tl.progress();
            setProgress(countdownProgress);
            if (countdownProgress === 0) {
              setShowImageIndex(prev => (prev + 1) % cardData.length);
              tl.restart();
            }
          },
        },
      );
    },
    { dependencies: [cardData.length], scope: containerRef },
  );

  const handleCardClick = (index: number) => {
    setShowImageIndex(index);
    if (timelineRef.current) {
      // stop the timeline when the user clicks on a card
      timelineRef.current.pause();
      setProgress(1);
    }
  };

  return (
    <div ref={containerRef} className="mt-6 grid grid-cols-12 gap-4 overflow-x-clip">
      <div className="col-span-6 grid gap-8">
        {cardData.map((card, index) => (
          <button
            key={index}
            className="grid grid-cols-[auto_1fr] gap-4 text-left"
            onClick={() => handleCardClick(index)}
          >
            <ProgressBar progress={showImageIndex === index ? progress : 0} isActive={showImageIndex === index} />
            <div className="flex flex-col gap-2">
              <h3 className="text-h6">{card.title}</h3>
              <p className="text-body text-gray-4">{card.body}</p>
            </div>
          </button>
        ))}
      </div>

      <div className="col-span-5 col-start-8">
        <CardScroller cardData={cardData} showImageIndex={showImageIndex} />
      </div>
    </div>
  );
}

function ProgressBar({ progress, isActive }: { progress: number; isActive: boolean }) {
  return (
    <div className={clsx('flex h-[67px] w-[6px] rounded bg-[#45494F]', isActive ? 'opacity-1' : 'opacity-0')}>
      <div className="w-full self-end rounded bg-gradle-green" style={{ height: `${progress * 100}%` }}></div>
    </div>
  );
}
